import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { IoArrowBack, IoBag } from 'react-icons/io5'

import Input from '@/components/forms/Input/Input'
import PasswordEyeInput from '@/components/forms/PasswordEyeInput/PasswordEyeInput'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import useAuthStore, { type SignupRequest } from '@/stores/AuthStore'
import routes from '@/routes/routes'

import './Register.css'

const Register = () => {
    const navigate = useNavigate()
    const { registerAccount, isLoading, isLogged } = useAuthStore()

    const [formData, setFormData] = useState<SignupRequest>({
        email: '',
        password1: '',
        password2: '',
    })

    useEffect(() => {
        if (isLogged) {
            navigate(routes.home)
        }
    }, [isLogged])

    const handleFormDataChange = (key: string, value: string) => {
        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const validateForm = (formData: SignupRequest) => {
        const { email, password1, password2 } = formData
        
        if (!email || !password1 || !password2) {
            return { isValid: false, message: 'Por favor complete todos los campos' }
        }
        
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return { isValid: false, message: 'Por favor ingrese una dirección de correo electrónico válida' }
        }
        
        // Password validation
        if (password1 !== password2) {
            return { isValid: false, message: 'Las contraseñas no coinciden' }
        }

        if (password1.length < 8) {
            return { isValid: false, message: 'La contraseña debe tener al menos 8 caracteres' }
        }
        
        return { isValid: true, message: '' }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const validation = validateForm(formData)
        if (!validation.isValid) {
            toast.error(validation.message)
            return
        }

        try {
            const status: boolean = await registerAccount(formData)
            if (status) {
                toast.success('¡Cuenta creada exitosamente! Por favor revise su correo electrónico para verificar su cuenta.')
                navigate(routes.verifyEmail)
                return
            }
            toast.error('Error en el registro. Por favor inténtelo de nuevo.')
        } catch (error) {
            toast.error('Error en el registro. Por favor inténtelo de nuevo.')
            console.error('Registration error:', error)
        }
    }

    return (
        <div className="auth_page">
            <div className="auth_container">
                <div className="auth_header">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(routes.home)}
                    >
                        <IoArrowBack size={16} />
                        Volver al Inicio
                    </Button>
                </div>

                <div className="auth_card">
                    <div className="auth_brand">
                        <div className="brand_logo">
                            <IoBag size={32} />
                        </div>
                        <h1>Crear Cuenta</h1>
                        <p>Únase a Plastitex y comience a personalizar sus productos</p>
                    </div>

                    <form className="auth_form" onSubmit={handleSubmit}>
                        <Input
                            name="email"
                            value={formData.email}
                            setValue={(value) => handleFormDataChange('email', value)}
                            label="Dirección de Correo Electrónico"
                            placeholder="Ingrese su correo electrónico"
                            type="email"
                            required
                        />
                        <PasswordEyeInput
                            name="password1"
                            value={formData.password1}
                            setValue={(value) => handleFormDataChange('password1', value)}
                            label="Contraseña"
                            placeholder="Cree una contraseña"
                        />
                        <PasswordEyeInput
                            name="password2"
                            value={formData.password2}
                            setValue={(value) => handleFormDataChange('password2', value)}
                            label="Confirmar Contraseña"
                            placeholder="Confirme su contraseña"
                        />
                        <Button
                            variant="primary"
                            size="lg"
                            type="submit"
                            disabled={isLoading}
                            className="auth_submit"
                        >
                            {isLoading ? <Spinner size="sm" variant="secondary" /> : 'Crear Cuenta'}
                        </Button>
                    </form>

                    <div className="auth_footer">
                        <p>
                            ¿Ya tiene una cuenta?{' '}
                            <button 
                                type="button"
                                className="auth_link"
                                onClick={() => navigate(routes.login)}
                            >
                                Iniciar Sesión
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Register
